// Router root. Dashboard + Project routes share the Sidebar via Layout; Login
// and NotFound render bare to keep the SSO entry-page clean.

import { useEffect } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { Redirect, Route, Switch, useLocation } from "wouter-preact";
import { Login } from "./routes/Login";
import { NotFound } from "./routes/NotFound";
import { Project } from "./routes/Project";
import { Settings } from "./routes/Settings";
import { MyTasks } from "./routes/MyTasks";
import { Today } from "./routes/Today";
import { Lists } from "./routes/Lists";
import { Favorites } from "./routes/Favorites";
import { Projects } from "./routes/Projects";
import { Dashboard } from "./routes/Dashboard";
import { Admin } from "./routes/Admin";
import { Goals } from "./routes/Goals";
import { Inbox } from "./routes/Inbox";
import { FollowUps } from "./routes/FollowUps";
import { ExternalLogin } from "./routes/ExternalLogin";
import { GuestTaskView } from "./routes/GuestTaskView";
import { GuestSettings } from "./routes/GuestSettings";
import { IntakeFormPage } from "./routes/IntakeFormPage";
import {
  CommandPalette,
  closePalette,
  openPalette,
  paletteInitialQuery,
  paletteOpen,
} from "./components/CommandPalette";
import { HotkeyHelp } from "./components/HotkeyHelp";
import { NewProjectModal } from "./components/NewProjectModal";
import { TaskImportHost } from "./components/TaskImportDialog";
import { CarryOverStepsDialog } from "./components/CarryOverStepsDialog";
import { useHotkeys } from "./lib/hotkeys";
import { startStream } from "./lib/sse";
import { refreshNotifications } from "./store/notifications";
import { useMe } from "./lib/auth";
import { startPath } from "./lib/navigation";
import { CaptureDialog } from "./components/CaptureDialog";
import { MobileCaptureFab } from "./components/MobileCaptureFab";
import { captureOpen, openCapture } from "./store/capture";
import { uxEnabled } from "./lib/ux";

export function App(): preact.JSX.Element {
  const helpOpen = useSignal(false);
  const showCreateProject = useSignal(false);
  const [, setLocation] = useLocation();
  const me = useMe();

  // Global SSE stream — user channel (projects:[]).
  // Only started when the user is authenticated (me.user is set). This
  // prevents the boot-time mintStreamTicket() POST firing on /login, which
  // would cause a 401 -> redirect -> remount -> infinite loop.
  // The effect re-runs when auth state resolves so the stream connects
  // immediately after login without a full page reload.
  // Externe Gäste sind ausgenommen: /api/stream/ticket ist für sie 403, was
  // den mintStreamTicket()-POST in einen endlosen Reconnect-Loop mit
  // "Forbidden for external guests"-Toasts treiben würde.
  useEffect(() => {
    if (!me.user || me.user.is_external) return;
    const stop = startStream({
      projects: [],
      onEvent: (type) => {
        if (type === "notification.created") {
          void refreshNotifications();
          // Notify the Inbox page so it can re-fetch if mounted.
          window.dispatchEvent(new Event("juuplan:inbox-refresh"));
        }
      },
    });
    return stop;
  }, [me.user]);

  // Global Cmd+K / Ctrl+K — opens the search palette anywhere.
  // Kept as a separate listener so it also fires when focus is inside an
  // input (the hotkey hook intentionally ignores editable targets).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        openPalette();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Single-key + g-chord hotkeys (ignores editable targets and modifiers).
  useHotkeys({
    onSearch: openPalette,
    onHelp: () => {
      helpOpen.value = !helpOpen.value;
    },
    onNavigate: (path) => setLocation(path),
    // `c` opens the palette pre-filtered to the "Neues Projekt" action
    // (see `paletteInitialQuery` / `openPalette`'s `initialQuery` param).
    onCreate: () => openPalette("neues projekt"),
    // UX-Vorschlag P2: N öffnet die globale Schnellerfassung — nur mit Flag
    // `ux_web_p2` (sonst wie bisher wirkungslos). Eingabefelder sind über
    // lib/hotkeys.ts ausgenommen.
    onCapture: () => {
      if (uxEnabled("ux_web_p2")) openCapture();
    },
  });

  return (
    <>
      <Switch>
        {/* Startziel haengt an ux_flags aus /api/me (P1): erst nach dem Laden
            entscheiden, sonst gilt der Code-Standard „vorschau" und ein
            Flag off/an greift beim Start nicht. */}
        <Route path="/" component={() => (me.loading ? null : <Redirect to={startPath()} />)} />
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/projects" component={Projects} />
        {/* routes/Home.tsx entfernt (Waise, von nirgendwo verlinkt); alte
            /home-Links landen weiter auf einem gültigen Ziel statt 404. */}
        <Route path="/home" component={() => (me.loading ? null : <Redirect to={startPath()} />)} />
        <Route path="/external" component={ExternalLogin} />
        <Route path="/guest-task" component={GuestTaskView} />
        <Route path="/guest-settings" component={GuestSettings} />
        <Route path="/intake/:token">
          {(params: { token: string }) => <IntakeFormPage params={params} />}
        </Route>
        <Route path="/login" component={Login} />
        <Route path="/settings" component={Settings} />
        <Route path="/admin/:section?">
          {(params: { section?: string }) => <Admin section={params.section} />}
        </Route>
        <Route path="/my-tasks" component={MyTasks} />
        <Route path="/today" component={Today} />
        <Route path="/lists/:id?">
          {(params: { id?: string }) => <Lists id={params.id} />}
        </Route>
        <Route path="/favorites" component={Favorites} />
        <Route path="/goals" component={Goals} />
        <Route path="/inbox" component={Inbox} />
        <Route path="/follow-ups" component={FollowUps} />
        <Route path="/p/:id">
          {(params: { id: string }) => <Project params={params} />}
        </Route>
        <Route component={NotFound} />
      </Switch>
      {paletteOpen.value && (
        <CommandPalette
          onClose={closePalette}
          onShowHelp={() => { helpOpen.value = true; }}
          onCreateProject={() => { showCreateProject.value = true; }}
          initialQuery={paletteInitialQuery.value}
        />
      )}
      {helpOpen.value && <HotkeyHelp onClose={() => { helpOpen.value = false; }} />}
      {showCreateProject.value && (
        <NewProjectModal
          onClose={() => { showCreateProject.value = false; }}
          onCreated={(id) => {
            showCreateProject.value = false;
            setLocation(`/p/${id}`);
          }}
        />
      )}
      {/* #826: genau EINMAL gemountet — Listen- und Detailansicht öffnen ihn
          beide über carryOverStepsSig, statt die Maske zweimal zu bauen. */}
      <CarryOverStepsDialog />
      {/* #1297: KI-Listenimport, EINMAL gemountet, geöffnet über store/taskImport. */}
      <TaskImportHost />
      {captureOpen.value && uxEnabled("ux_web_p2") && <CaptureDialog />}
      {/* P7: mobiler +-Knopf (nur unter 768 px, nur auf Seiten mit Kontext). */}
      {!captureOpen.value && uxEnabled("ux_web_p7") && uxEnabled("ux_web_p2") && me.user && !me.user.is_external && (
        <MobileCaptureFab />
      )}
    </>
  );
}
